/**
 * Dynamic font loader — downloads custom fonts from Google Fonts at runtime.
 *
 * Called in two places:
 *   1. At app startup (index.tsx) — loads the default from fonts.css
 *   2. After project sync (Commonfunction.js) — loads the project's configured font
 *
 * Flow:
 *   - Accepts a font name (or reads --font-primary if none is provided)
 *   - If the font is not "Roboto" (already bundled via @fontsource/roboto),
 *     injects a <link> tag to Google Fonts CDN to download it
 *   - On network error, offline state, or timeout, Roboto is used as fallback
 *   - Only one Google Fonts <link> exists at a time (previous is removed)
 */
import { isValidFontName } from "@helpers/fontConfig";

const FONT_LOAD_TIMEOUT_MS = 3000;

/**
 * Reads --font-primary from the document root and extracts the font name
 * (before the fallback comma). Performs a synchronous style read — callers
 * on hot paths should resolve the name once and pass it via the parameter.
 */
export function readPrimaryFontName(): string {
    const raw = getComputedStyle(document.documentElement)
        .getPropertyValue("--font-primary")
        .trim();
    return raw.replace(/["']/g, "").split(",")[0].trim();
}

export function loadConfiguredFont(fontName?: string): void {
    const resolvedName = (fontName ?? readPrimaryFontName()).trim();

    // Roboto est deja bundle, pas besoin de telecharger
    if (!resolvedName || resolvedName.toLowerCase() === "roboto") {
        return;
    }

    // Validation stricte: bloque toute entree inattendue (caracteres URL, etc.)
    if (!isValidFontName(resolvedName)) {
        console.warn(
            `[fontLoader] Nom de police invalide "${resolvedName}", chargement ignore.`
        );
        return;
    }

    // Hors-ligne: on court-circuite, Roboto bundle prend le relais
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
        console.warn(
            `[fontLoader] Hors-ligne, chargement de "${resolvedName}" ignore.`
        );
        return;
    }

    // Verifier si un link Google Fonts existe deja
    const existingLink = document.querySelector(
        'link[data-font-loader="true"]'
    );
    if (existingLink) {
        existingLink.remove();
    }

    // Creer le lien Google Fonts
    const encodedFont = encodeURIComponent(resolvedName);
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?family=${encodedFont}:wght@300;400;500;600;700&display=swap`;
    link.setAttribute("data-font-loader", "true");

    let settled = false;
    const timeoutId = window.setTimeout(() => {
        if (settled) return;
        settled = true;
        console.warn(
            `[fontLoader] Timeout sur "${resolvedName}" apres ${FONT_LOAD_TIMEOUT_MS}ms, Roboto sera utilise.`
        );
        link.remove();
    }, FONT_LOAD_TIMEOUT_MS);

    link.onload = () => {
        settled = true;
        window.clearTimeout(timeoutId);
    };

    link.onerror = () => {
        settled = true;
        window.clearTimeout(timeoutId);
        console.warn(
            `[fontLoader] Impossible de charger "${resolvedName}" depuis Google Fonts. Roboto sera utilise.`
        );
        link.remove();
    };

    document.head.appendChild(link);
}
